import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import CasLink from 'src/compass/shared/components/CasLink/CasLink';
import useUpdateMutation from 'src/compass/shared/hooks/reactQuery/useUpdateMutation';
import { onSuccessEnqueueSnackbar } from 'src/compass/shared/util/onSuccessEnqueueSnackbar';
import infoUriFromEntity from 'src/compass/shared/util/infoUriFromEntity';
import {
  TouchpointFrequencyEditSelect,
  UnassignedOption_Name,
  useTouchpointFrequencyOptions,
} from './TouchpointFrequencySelect';
import { TOUCHPOINT_ENTITY } from './TouchpointsTab';

interface IProps {
  defaultValue: string;
  id: number;
  onUpdated: (nextTouchpointDate: Date) => void;
}

interface ITouchpointCacheParams {
  id: number;
  nextTouchpointDate?: Date;
  isPending?: boolean;
}

export const useTouchpointCache = () => {
  const queryClient = useQueryClient();

  const updateTouchpointListing = ({
    id,
    nextTouchpointDate,
    isPending,
  }: ITouchpointCacheParams) => {
    queryClient.setQueriesData(
      { queryKey: [infoUriFromEntity(TOUCHPOINT_ENTITY)] },
      (oldData: any) => {
        if (!oldData?.result) return oldData;
        return {
          ...oldData,
          result: oldData.result.map((item: any) =>
            item.id === id
              ? {
                  ...item,
                  ...(nextTouchpointDate !== undefined && { nextTouchpointDate }),
                  _isPending: isPending,
                }
              : item
          ),
        };
      }
    );
  };

  const updateTouchpointDetails = ({
    id,
    nextTouchpointDate,
    isPending,
  }: ITouchpointCacheParams) => {
    queryClient.setQueriesData({ queryKey: [`${TOUCHPOINT_ENTITY}/${id}`] }, (oldData: any) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        ...(nextTouchpointDate !== undefined && { nextTouchpointDate }),
        _isPending: isPending,
      };
    });
  };

  const updateHandoffRequested = ({
    id,
    handoffRequested,
    handoffRequestedByCurrentUser,
  }: {
    id: number;
    handoffRequested: boolean;
    handoffRequestedByCurrentUser?: boolean;
  }) => {
    queryClient.setQueriesData(
      { queryKey: [infoUriFromEntity(TOUCHPOINT_ENTITY)] },
      (oldData: any) => {
        if (!oldData?.result) return oldData;
        return {
          ...oldData,
          result: oldData.result.map((item: any) =>
            item.id === id
              ? {
                  ...item,
                  handoffRequested,
                  ...(handoffRequestedByCurrentUser !== undefined && {
                    handoffRequestedByCurrentUser,
                  }),
                }
              : item
          ),
        };
      }
    );
  };

  return { updateTouchpointListing, updateTouchpointDetails, updateHandoffRequested };
};

export default function RenderTouchpointFrequency({ defaultValue, id, onUpdated }: IProps) {
  const [frequencyVal, setFrequencyVal] = useState(defaultValue);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const { updateTouchpointListing, updateTouchpointDetails } = useTouchpointCache();

  const { options } = useTouchpointFrequencyOptions({});

  const { mutate } = useUpdateMutation({
    url: 'Utilities/EntityTouchpoint/UpdateFrequency',
    showLoader: false,
    onSuccess: (response: any) => {
      onSuccessEnqueueSnackbar(response);
      onUpdated(response.nextTouchpointDate);
    },
  });

  const frequencyName =
    frequencyVal == null
      ? UnassignedOption_Name
      : options.find((opt: any) => opt.id === frequencyVal)?.name ?? frequencyVal;

  if (isEditing) {
    return (
      <TouchpointFrequencyEditSelect
        label=""
        value={frequencyVal}
        onChange={(_, value) => {
          setFrequencyVal(value);
          updateTouchpointListing({ id, isPending: true });
          updateTouchpointDetails({ id, isPending: true });
          mutate(
            { id, frequency: value },
            {
              onError: () => {
                setFrequencyVal(defaultValue);
                updateTouchpointListing({ id, isPending: false });
                updateTouchpointDetails({ id, isPending: false });
              },
            }
          );
          setIsEditing(false);
        }}
      />
    );
  }

  return <CasLink onClick={() => setIsEditing(true)}>{frequencyName}</CasLink>;
}
